import { apiFetch, apiFetchNullable, ApiError, useMockApi } from '@/lib/api/client'
import { mapCompareBatch, mapSessionSettings } from '@/lib/api/mappers'
import { buildSettingsBadge } from '@/lib/chat/settings-badge'
import { createDemoCompareBatch } from '@/lib/compare/mock-data'
import { readSseStream } from '@/lib/sse'
import type { SessionSettings } from '@/types/chat'
import type { CompareBatch, StreamCompareHandlers } from '@/types/compare'

const COMPARE_STORAGE_KEY = 'modelscout_mock_compare_batches'

function loadBatches(): Record<string, CompareBatch> {
  if (typeof sessionStorage === 'undefined') {
    return { [createDemoCompareBatch().id]: createDemoCompareBatch() }
  }

  const raw = sessionStorage.getItem(COMPARE_STORAGE_KEY)
  if (!raw) {
    const demo = createDemoCompareBatch()
    const initial = { [demo.id]: demo }
    sessionStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(initial))
    return initial
  }

  try {
    return JSON.parse(raw) as Record<string, CompareBatch>
  } catch {
    const demo = createDemoCompareBatch()
    return { [demo.id]: demo }
  }
}

function saveBatches(batches: Record<string, CompareBatch>) {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(batches))
}

function toSettingsPayload(settings?: SessionSettings) {
  if (!settings) return undefined

  return {
    temperature: settings.temperature,
    top_p: settings.topP,
    max_tokens: settings.maxTokens,
    json_mode: settings.jsonMode,
  }
}

function mapStreamMeta(payload: Record<string, unknown>): CompareBatch {
  const responses = Array.isArray(payload.responses) ? payload.responses : []

  return {
    id: String(payload.compare_batch_id),
    title: typeof payload.title === 'string' ? payload.title : undefined,
    promptText: String(payload.prompt_text ?? ''),
    createdAt: String(payload.created_at ?? new Date().toISOString()),
    responses: responses.map((item) => {
      const record = item as Record<string, unknown>
      const model = (record.model ?? {}) as Record<string, unknown>
      const provider = (record.provider ?? {}) as Record<string, unknown>
      const category = record.category ? (record.category as Record<string, unknown>) : null
      const settingsSnapshot = mapSessionSettings(record.settings_snapshot)

      return {
        modelResponseId: String(record.model_response_id),
        sessionId: String(record.session_id),
        model: {
          displayName: String(model.display_name ?? ''),
          slug: String(model.slug ?? ''),
        },
        provider: { name: String(provider.name ?? '') },
        category: category ? { name: String(category.name ?? '') } : undefined,
        settingsSnapshot,
        settingsBadge: buildSettingsBadge(settingsSnapshot),
        normalizedText: '',
        status: String(record.status ?? 'pending'),
        rating: null,
      }
    }),
  }
}

export interface CompareApi {
  getBatch(batchId: string): Promise<CompareBatch | null>
  sendCompare(input: SendCompareInput): Promise<SendCompareResult>
  sendCompareStream(input: SendCompareInput, handlers: StreamCompareHandlers): Promise<void>
  updateResponseRating(
    batchId: string,
    modelResponseId: string,
    rating: CompareBatch['responses'][number]['rating'],
  ): Promise<CompareBatch | null>
}

export interface SendCompareInput {
  sessionIds: string[]
  promptText: string
  title?: string
  settingsOverride?: SessionSettings
  signal?: AbortSignal
}

export interface SendCompareResult {
  compareBatchId: string
  promptRequestId: string
  responseIds: string[]
}

class MockCompareApi implements CompareApi {
  private batches = loadBatches()

  async getBatch(batchId: string): Promise<CompareBatch | null> {
    return structuredClone(this.batches[batchId] ?? null)
  }

  async sendCompare(input: SendCompareInput): Promise<SendCompareResult> {
    if (input.sessionIds.length < 2) throw new Error('compare_requires_at_least_two_sessions')

    const batchId = `batch-${crypto.randomUUID()}`
    const settings = input.settingsOverride ?? {
      temperature: 0.5,
      topP: 0.9,
      maxTokens: 4096,
      jsonMode: false,
    }

    const batch: CompareBatch = {
      id: batchId,
      title: input.title ?? 'Compare batch',
      promptText: input.promptText,
      createdAt: new Date().toISOString(),
      responses: input.sessionIds.map((sessionId, index) => ({
        modelResponseId: `resp-${crypto.randomUUID()}`,
        sessionId,
        model: { displayName: `Model ${index + 1}`, slug: `model-${index + 1}` },
        provider: { name: 'OpenRouter' },
        category: { name: 'Coding' },
        settingsSnapshot: settings,
        settingsBadge: buildSettingsBadge(settings),
        normalizedText: `Mock compare response for session ${sessionId}: ${input.promptText.slice(0, 80)}`,
        tokensInput: 12,
        tokensOutput: 80 + index * 10,
        estimatedCost: index === 2 ? 0 : 0.00001,
        latencyMs: 800 + index * 100,
        status: 'completed',
        rating: null,
      })),
    }

    this.batches[batchId] = batch
    saveBatches(this.batches)

    return {
      compareBatchId: batchId,
      promptRequestId: `req-${crypto.randomUUID()}`,
      responseIds: batch.responses.map((response) => response.modelResponseId),
    }
  }

  async sendCompareStream(input: SendCompareInput, handlers: StreamCompareHandlers): Promise<void> {
    if (input.sessionIds.length < 2) throw new Error('compare_requires_at_least_two_sessions')

    const batchId = `batch-${crypto.randomUUID()}`
    const settings = input.settingsOverride ?? {
      temperature: 0.5,
      topP: 0.9,
      maxTokens: 4096,
      jsonMode: false,
    }

    const batch: CompareBatch = {
      id: batchId,
      title: input.title ?? 'Compare batch',
      promptText: input.promptText,
      createdAt: new Date().toISOString(),
      responses: input.sessionIds.map((sessionId, index) => ({
        modelResponseId: `resp-${crypto.randomUUID()}`,
        sessionId,
        model: { displayName: `Model ${index + 1}`, slug: `model-${index + 1}` },
        provider: { name: 'OpenRouter' },
        category: { name: 'Coding' },
        settingsSnapshot: settings,
        settingsBadge: buildSettingsBadge(settings),
        normalizedText: '',
        status: 'pending',
        rating: null,
      })),
    }

    handlers.onMeta({ batch })

    await new Promise((resolve) => setTimeout(resolve, 100))

    await Promise.all(
      batch.responses.map(async (response, index) => {
        const parts = [
          `Mock compare ${index + 1}: `,
          input.promptText.slice(0, 40),
          ' (streamed)',
        ]
        let text = ''

        for (const part of parts) {
          await new Promise((resolve) => setTimeout(resolve, 5))
          text += part
          handlers.onChunk(response.modelResponseId, part)
        }

        const done = {
          modelResponseId: response.modelResponseId,
          normalizedText: text,
          tokensInput: 12,
          tokensOutput: 80 + index * 10,
          estimatedCost: index === 2 ? 0 : 0.00001,
          latencyMs: 420 + index * 50,
          status: 'completed',
        }

        handlers.onResponseDone(done)

        response.normalizedText = text
        response.tokensInput = done.tokensInput
        response.tokensOutput = done.tokensOutput
        response.estimatedCost = done.estimatedCost
        response.latencyMs = done.latencyMs
        response.status = done.status
      }),
    )

    this.batches[batchId] = batch
    saveBatches(this.batches)
    handlers.onDone(batchId)
  }

  async updateResponseRating(
    batchId: string,
    modelResponseId: string,
    rating: CompareBatch['responses'][number]['rating'],
  ): Promise<CompareBatch | null> {
    const batch = this.batches[batchId]
    if (!batch) return null

    batch.responses = batch.responses.map((response) => {
      if (response.modelResponseId === modelResponseId) {
        return { ...response, rating }
      }

      if (rating?.isBestAnswer) {
        return {
          ...response,
          rating: response.rating
            ? { ...response.rating, isBestAnswer: false }
            : response.rating,
        }
      }

      return response
    })

    this.batches[batchId] = batch
    saveBatches(this.batches)
    return structuredClone(batch)
  }
}

class HttpCompareApi implements CompareApi {
  async getBatch(batchId: string): Promise<CompareBatch | null> {
    const body = await apiFetchNullable<unknown>(`/api/v1/compare/${batchId}`)
    return body ? mapCompareBatch(body) : null
  }

  async sendCompare(input: SendCompareInput): Promise<SendCompareResult> {
    const body = await apiFetch<{
      compare_batch_id: string
      prompt_request_id: string
      request_ids: string[]
    }>('/api/v1/compare/send', {
      method: 'POST',
      body: JSON.stringify({
        session_ids: input.sessionIds,
        prompt_text: input.promptText,
        title: input.title,
        settings_override: toSettingsPayload(input.settingsOverride),
        stream: false,
      }),
    })

    return {
      compareBatchId: body.compare_batch_id,
      promptRequestId: body.prompt_request_id,
      responseIds: body.request_ids,
    }
  }

  async sendCompareStream(input: SendCompareInput, handlers: StreamCompareHandlers): Promise<void> {
    const response = await fetch('/api/v1/compare/send', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        session_ids: input.sessionIds,
        prompt_text: input.promptText,
        title: input.title,
        settings_override: toSettingsPayload(input.settingsOverride),
        stream: true,
      }),
      signal: input.signal,
    })

    if (response.status === 401) {
      throw new ApiError('unauthorized', 401)
    }

    if (!response.ok) {
      let code: string | undefined
      try {
        const body = (await response.json()) as { error?: string }
        code = body.error
      } catch {
        // ignore
      }
      throw new ApiError(code ?? `http_${response.status}`, response.status, code)
    }

    await readSseStream(response, ({ event, data }) => {
      const payload = JSON.parse(data) as Record<string, unknown>

      if (event === 'meta') {
        handlers.onMeta({ batch: mapStreamMeta(payload) })
        return
      }

      if (event === 'chunk') {
        handlers.onChunk(String(payload.model_response_id), String(payload.delta ?? ''))
        return
      }

      if (event === 'response_done') {
        handlers.onResponseDone({
          modelResponseId: String(payload.model_response_id),
          normalizedText: String(payload.normalized_text ?? ''),
          tokensInput: Number(payload.tokens_input ?? 0),
          tokensOutput: Number(payload.tokens_output ?? 0),
          estimatedCost: Number(payload.estimated_cost ?? 0),
          latencyMs: Number(payload.latency_ms ?? 0),
          status: String(payload.status ?? 'completed'),
          errorMessage:
            typeof payload.error_message === 'string' ? payload.error_message : undefined,
        })
        return
      }

      if (event === 'done') {
        handlers.onDone(String(payload.compare_batch_id))
        return
      }

      if (event === 'cancelled') {
        handlers.onDone(String(payload.compare_batch_id ?? ''))
        return
      }

      if (event === 'error') {
        handlers.onError(String(payload.error ?? 'stream_error'))
      }
    })
  }

  async updateResponseRating(
    batchId: string,
    modelResponseId: string,
    rating: CompareBatch['responses'][number]['rating'],
  ): Promise<CompareBatch | null> {
    const batch = await this.getBatch(batchId)
    if (!batch || !rating) return batch

    return {
      ...batch,
      responses: batch.responses.map((response) => {
        if (response.modelResponseId === modelResponseId) {
          return { ...response, rating }
        }

        if (rating.isBestAnswer) {
          return {
            ...response,
            rating: response.rating
              ? { ...response.rating, isBestAnswer: false }
              : response.rating,
          }
        }

        return response
      }),
    }
  }
}

export const compareApi: CompareApi = useMockApi() ? new MockCompareApi() : new HttpCompareApi()

export function resetMockCompareStorage() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(COMPARE_STORAGE_KEY)
}
